import EmailIcon from '@mui/icons-material/Email';
import { Alert, Box, Button, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import AvanzuHeader from '../../components/AvanzuHeader';

const VerifyEmailPending: React.FC = () => {
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [manualEmail, setManualEmail] = useState('');
  const email = decodeURIComponent(new URLSearchParams(window.location.search).get('email') || '');

  const handleResendEmail = async () => {
    const targetEmail = manualEmail === '' ? email : manualEmail;
    if (!targetEmail) return;

    setIsResending(true);
    setMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail }),
      });

      const data = await response.json();

      setMessage({
        type: data.success ? 'success' : 'error',
        text: data.message || data.error || 'Unknown error occurred',
      });
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      setMessage({
        type: 'error',
        text: 'Failed to resend verification email. Please try again.',
      });
    } finally {
      setIsResending(false);
    }
  };

  // No email in URL - show manual entry form
  if (!email) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <AvanzuHeader />
          </Box>
          <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Check Your Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please click the verification link in your email to activate your account. Don't
                forget to check your spam folder! Didn't receive it? Enter your email address below
                to resend the verification link.
              </Typography>
            </Box>

            <Stack spacing={2}>
              <TextField
                name="email"
                id="verifyEmail"
                label="Email Address"
                type="email"
                autoComplete="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                fullWidth
                variant="outlined"
              />

              {message && <Alert severity={message.type}>{message.text}</Alert>}

              <Button
                variant="contained"
                onClick={() => handleResendEmail()}
                disabled={isResending || !manualEmail}
                sx={{ mt: 2 }}
              >
                {isResending ? 'Sending...' : 'Send Verification Email'}
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                The verification link will expire in 48 hours.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Don't have an account? <a href="/signup">Create an account here</a>.
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Email present in URL - show normal verification pending UI
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <AvanzuHeader />
        </Box>
        <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Check Your Email
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We've sent a verification link to:
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, wordBreak: 'break-word' }}>
              {email}
            </Typography>
          </Box>

          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Please click the verification link in your email to activate your account. Don't
              forget to check your spam folder!
            </Typography>

            {message && <Alert severity={message.type}>{message.text}</Alert>}

            <Button
              variant="outlined"
              onClick={() => handleResendEmail()}
              disabled={isResending}
              sx={{ mt: 2 }}
            >
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              The verification link will expire in 48 hours.
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmailPending;
